import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import FormSchemaEditor from './admin/FormSchemaEditor';
import { generateStrategyPrompt } from '../utils/promptGenerator';
import { sendStrategyEmail } from '../services/emailService';

const AdminDashboard = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('submissions');
  const [forms, setForms] = useState([]);
  const [schema, setSchema] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForm, setSelectedForm] = useState(null);
  const [copying, setCopying] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [formsRes, schemaRes] = await Promise.all([
        supabase.from('growth_forms_2024').select('*').order('updated_at', { ascending: false }),
        supabase.from('form_schema_2024').select('schema_data').maybeSingle()
      ]);

      if (formsRes.error) throw formsRes.error;
      setForms(formsRes.data || []);
      if (schemaRes.data?.schema_data) setSchema(schemaRes.data.schema_data);
    } catch (err) {
      console.error("Fetch Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPrompt = () => {
    if (!selectedForm || !schema.length) return;
    const prompt = generateStrategyPrompt(selectedForm.form_data, schema);
    navigator.clipboard.writeText(prompt);
    setCopying(true);
    setTimeout(() => setCopying(false), 2000);
  };

  const handleSendEmail = async () => {
    if (!selectedForm || !schema.length) return;
    setSendingEmail(true);
    const prompt = generateStrategyPrompt(selectedForm.form_data, schema);
    const result = await sendStrategyEmail(
      selectedForm.email,
      selectedForm.form_data?.businessName || 'Your Business',
      prompt
    );
    if (result.usedFallback) {
      alert("Function not deployed yet. Opening your email client instead.");
    } else if (result.success) {
      alert("Strategy sent successfully!");
    }
    setSendingEmail(false);
  };

  const triggerDownload = (content, filename, type = 'text/csv') => {
    const blob = new Blob([content], { type: `${type};charset=utf-8;` });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadSubmissionsCSV = () => {
    if (forms.length === 0) return;
    const allKeys = Array.from(new Set(forms.flatMap(f => Object.keys(f.form_data || {}))));
    const headers = ['Email', 'Status', 'Last Updated', ...allKeys];
    const csvContent = [
      headers.join(','),
      ...forms.map(f => [
        f.email,
        f.is_completed ? 'Completed' : 'Draft',
        new Date(f.updated_at).toLocaleDateString(),
        ...allKeys.map(key => `"${(f.form_data[key] || '').toString().replace(/"/g, '""')}"`)
      ].join(','))
    ].join('\n');
    triggerDownload(csvContent, `growth_submissions_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const downloadSchemaJSON = () => {
    if (!schema.length) return;
    const jsonContent = JSON.stringify(schema, null, 2);
    triggerDownload(jsonContent, `form_schema_export_${new Date().toISOString().split('T')[0]}.json`, 'application/json');
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Permanently delete this submission?")) return;
    setDeletingId(id);
    try {
      const { error: deleteError } = await supabase.from('growth_forms_2024').delete().eq('id', id);
      if (deleteError) throw deleteError;
      setForms(forms.filter(f => f.id !== id));
    } catch (err) {
      alert("Failed to delete.");
    } finally {
      setDeletingId(null);
    }
  };

  const getLabel = (key) => {
    for (const section of schema) {
      const field = section.fields?.find(f => f.name === key);
      if (field) return field.label;
    }
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const filteredForms = forms.filter(f => 
    f.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.form_data?.businessName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center gap-4">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-blue-400 font-black tracking-widest uppercase text-xs animate-pulse">Syncing Intelligence...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6">
          <div className="flex items-center gap-5">
            <button onClick={onBack} className="p-3.5 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all active:scale-95">
              <SafeIcon name="ArrowLeft" className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-3xl font-black text-slate-900 tracking-tight">Admin Intelligence</h1>
              <p className="text-slate-500 text-sm font-medium">Monitoring {forms.length} active business profiles</p>
            </div>
          </div>

          <div className="flex bg-white p-1.5 rounded-[20px] border border-slate-200 shadow-sm">
            <button 
              onClick={() => setActiveTab('submissions')}
              className={`px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all ${activeTab === 'submissions' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'}`}
            >
              Submissions
            </button>
            <button 
              onClick={() => setActiveTab('schema')}
              className={`px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all ${activeTab === 'schema' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'}`}
            >
              Form Builder
            </button>
          </div>
        </div>

        {activeTab === 'submissions' ? (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <SafeIcon name="Search" className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input 
                  type="text" 
                  placeholder="Search by email, business name..." 
                  className="w-full pl-14 pr-6 py-4.5 bg-white border border-slate-200 rounded-[22px] outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-medium text-slate-900 shadow-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={downloadSchemaJSON}
                  className="px-6 py-4.5 bg-white border border-slate-200 rounded-[22px] font-bold text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm"
                >
                  <SafeIcon name="FileCode" className="w-4 h-4" />
                  Export Schema
                </button>
                <button 
                  onClick={downloadSubmissionsCSV}
                  className="px-6 py-4.5 bg-white border border-slate-200 rounded-[22px] font-bold text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm"
                >
                  <SafeIcon name="Download" className="w-4 h-4" />
                  Export CSV
                </button>
                <button 
                  onClick={fetchData}
                  className="px-6 py-4.5 bg-slate-900 text-white rounded-[22px] font-bold hover:bg-slate-800 transition-all flex items-center gap-2 shadow-xl"
                >
                  <SafeIcon name="RefreshCw" className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>

            <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                  <tr>
                    <th className="px-10 py-6">Respondent</th>
                    <th className="px-10 py-6">Status</th>
                    <th className="px-10 py-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredForms.map(form => (
                    <tr key={form.id} className="hover:bg-blue-50/20 transition-colors group">
                      <td className="px-10 py-8">
                        <div className="flex flex-col">
                          <span className="font-black text-slate-900 text-lg group-hover:text-blue-600 transition-colors">{form.email}</span>
                          <span className="text-sm text-slate-400 font-bold tracking-tight">{form.form_data?.businessName || 'Undefined Entity'}</span>
                        </div>
                      </td>
                      <td className="px-10 py-8">
                        <div className="flex items-center gap-2.5">
                          <span className={`w-2 h-2 rounded-full ${form.is_completed ? 'bg-emerald-500' : 'bg-amber-400 animate-pulse'}`} />
                          <span className={`text-[11px] font-black uppercase tracking-widest ${form.is_completed ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {form.is_completed ? 'Finalized' : 'Active Draft'}
                          </span>
                        </div>
                      </td>
                      <td className="px-10 py-8 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button 
                            onClick={() => setSelectedForm(form)}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-[18px] font-black text-xs hover:bg-slate-900 hover:text-white transition-all border border-slate-200 shadow-sm"
                          >
                            <SafeIcon name="Eye" className="w-4 h-4" />
                            View
                          </button>
                          <button 
                            onClick={(e) => handleDelete(form.id, e)}
                            className="p-3 rounded-[18px] text-slate-400 hover:bg-red-50 hover:text-red-600 transition-all"
                          >
                            <SafeIcon name="Trash2" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <FormSchemaEditor />
        )}
      </div>

      {/* Detail Modal */}
      {selectedForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10">
          <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-xl" onClick={() => setSelectedForm(null)} />
          <div className="relative bg-white w-full max-w-6xl h-full max-h-[90vh] rounded-[48px] flex flex-col overflow-hidden shadow-2xl border border-white/20">
            <div className="p-10 border-b flex flex-col lg:flex-row justify-between items-start lg:items-center bg-slate-50/50 gap-6">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h2 className="text-3xl font-black text-slate-900 tracking-tight">{selectedForm.email}</h2>
                  <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.15em] ${selectedForm.is_completed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {selectedForm.is_completed ? 'Submission' : 'Draft'}
                  </span>
                </div>
                <p className="text-slate-400 font-bold text-sm">Last Synced: {new Date(selectedForm.updated_at).toLocaleString()}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="flex items-center gap-2 px-6 py-4 bg-white border border-slate-200 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm"
                >
                  <SafeIcon name={sendingEmail ? "Loader" : "Mail"} className={`w-4 h-4 ${sendingEmail ? 'animate-spin' : ''}`} />
                  {sendingEmail ? 'Sending...' : 'Email Strategy'}
                </button>
                <button 
                  onClick={handleCopyPrompt}
                  className={`flex items-center gap-2 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${copying ? 'bg-emerald-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200'}`}
                >
                  <SafeIcon name={copying ? "Check" : "Zap"} className="w-4 h-4" />
                  {copying ? 'Prompt Copied!' : 'Copy AI Prompt'}
                </button>
                <button onClick={() => setSelectedForm(null)} className="p-4 bg-white border border-slate-200 rounded-3xl text-slate-400 hover:text-slate-900 shadow-sm">
                  <SafeIcon name="X" className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-10 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(selectedForm.form_data || {}).map(([key, value]) => (
                  <div key={key} className="bg-slate-50 p-8 rounded-[32px] border border-slate-100 group/card">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-3 group-hover/card:text-blue-500 transition-colors">{getLabel(key)}</label>
                    <p className="text-slate-900 font-bold text-lg leading-relaxed whitespace-pre-wrap">{value || 'Unanswered'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;